
import React, { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Upload, Pencil, Loader2, CheckCircle } from 'lucide-react';
import { getBusinessStatus } from '@/utils/businessUtils';
import { BusinessBadges } from '@/components/BusinessBadges';
import { cn } from '@/lib/utils';

interface BusinessHeaderProps {
  business: {
    id: string;
    name: string;
    company_banner_url?: string | null;
    company_logo?: string | null;
    is_active: boolean;
    is_listed: boolean;
  };
  onViewPublicPage: () => void;
  editMode?: boolean;
  onBannerUpload?: (file: File) => void;
  onLogoUpload?: (file: File) => void;
  uploading?: {
    banner: boolean;
    logo: boolean;
  };
  onShowListingConfirmation?: () => void;
}

const BusinessHeader: React.FC<BusinessHeaderProps> = ({ 
  business, 
  onViewPublicPage,
  editMode = false,
  onBannerUpload,
  onLogoUpload,
  uploading = { banner: false, logo: false },
  onShowListingConfirmation
}) => {
  const businessStatus = getBusinessStatus(business.is_active, business.is_listed);
  const [hoveredElement, setHoveredElement] = useState<'banner' | 'logo' | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  
  // Check if business needs listing confirmation
  const needsListingConfirmation = business.is_active && !business.is_listed;

  // Reset hover state when editMode changes
  useEffect(() => {
    if (!editMode) {
      setHoveredElement(null);
    }
  }, [editMode]);

  // Cleanup hover state on unmount
  useEffect(() => {
    return () => {
      setHoveredElement(null);
    };
  }, []);

  const handleBannerClick = () => {
    if (editMode && bannerInputRef.current) {
      bannerInputRef.current.click();
    }
  };

  const handleLogoClick = () => {
    if (editMode && logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onBannerUpload) {
      onBannerUpload(file);
      e.target.value = '';
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onLogoUpload) {
      onLogoUpload(file);
      e.target.value = '';
    }
  };

  return (
    <div className="relative mb-8">
      {/* Banner Image */}
      <div 
        className={cn(
          "relative h-48 md:h-64 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl overflow-hidden",
          editMode && "cursor-pointer"
        )}
        onClick={handleBannerClick}
        onMouseEnter={() => editMode && setHoveredElement('banner')}
        onMouseLeave={() => setHoveredElement(null)}
        onTouchStart={() => editMode && setHoveredElement('banner')}
        onTouchEnd={() => setHoveredElement(null)}
      >
        {business.company_banner_url ? (
          <img
            src={business.company_banner_url}
            alt={`${business.name} banner`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <span className="text-lg">No banner image</span>
          </div>
        )}

        {/* Edit Banner Overlay */}
        {editMode && (hoveredElement === 'banner' || uploading.banner) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity">
            {uploading.banner ? (
              <div className="flex items-center space-x-2 text-white">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Uploading banner...</span>
              </div>
            ) : (
              <Button variant="secondary" size="sm" className="bg-white/90 backdrop-blur-sm">
                <Upload className="h-4 w-4 mr-2" />
                Change Banner
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Business Info Card */}
      <div className="relative bg-white rounded-b-xl border border-gray-200 shadow-sm pt-16 md:pt-20 pb-6 px-6">
        {/* Business Logo - Positioned to cross banner/card boundary */}
        <div 
          className={cn(
            "absolute -top-16 left-6 w-24 h-24 md:w-32 md:h-32 bg-white rounded-full border-4 border-white shadow-lg overflow-hidden flex-shrink-0 z-10",
            editMode && "cursor-pointer"
          )}
          onClick={handleLogoClick}
          onMouseEnter={() => editMode && setHoveredElement('logo')}
          onMouseLeave={() => setHoveredElement(null)}
          onTouchStart={() => editMode && setHoveredElement('logo')}
          onTouchEnd={() => setHoveredElement(null)}
        >
          {business.company_logo ? (
            <img
              src={business.company_logo}
              alt={`${business.name} logo`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs text-center p-2">
              No logo
            </div>
          )}

          {/* Edit Logo Overlay */}
          {editMode && (hoveredElement === 'logo' || uploading.logo) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity rounded-full">
              {uploading.logo ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
                <Pencil className="h-4 w-4 text-white" />
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Business Name and Status */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 truncate leading-tight mb-0">
                {business.name}
              </h1>
              <Badge 
                variant="secondary" 
                className={cn("w-fit sm:mt-1", businessStatus.className)}
              >
                {businessStatus.label}
              </Badge>
            </div>
            <p className="text-gray-600 text-sm">
              Manage your business details and settings
            </p>
            
            {/* Business Achievement Badges */}
            <div className="mt-3">
              <BusinessBadges 
                businessId={business.id}
                mode="full"
                className="max-w-none"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex-shrink-0 space-y-3 sm:space-y-2">
            {/* Prominent Confirm Listing Button - only show when approved but not listed */}
            {needsListingConfirmation && onShowListingConfirmation && (
              <Button 
                onClick={onShowListingConfirmation}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold animate-pulse"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Your Listing
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={onViewPublicPage}
              className="w-full sm:w-auto"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Public Page
            </Button>
          </div>
        </div>
      </div>

      {/* Hidden File Inputs */}
      {editMode && (
        <>
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            onChange={handleBannerFileChange}
            className="hidden"
          />
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default BusinessHeader;
